import React from "react";
import PropTypes from "prop-types";
import { defineMessages, FormattedMessage, injectIntl, intlShape } from "react-intl";
import { Pagination } from "@yoast/ui-library";

import * as styles from "./styles.module.scss";

const messages = defineMessages( {
	showingResults: {
		id: "subscriptions.pagination.showingResults",
		defaultMessage: "Showing {from} to {to} of {total} results",
	},
	previousPage: {
		id: "subscriptions.pagination.previousPage",
		defaultMessage: "Previous page",
	},
	nextPage: {
		id: "subscriptions.pagination.nextPage",
		defaultMessage: "Next page",
	},
} );

/**
 * The pagination footer for the subscriptions overview.
 *
 * Reuses the prop shape and the "Showing X to Y of Z" line from the sites overview card, so the two
 * paginated tables in the app read and behave the same way.
 *
 * The counts come from the server's `meta` rather than from the rendered rows. That matters because the
 * rows are one page and `total` is the whole filtered result set, so deriving either from the other is
 * only correct on a single-page list — the case that would hide the bug.
 *
 * The range's upper bound is clamped to `total` rather than computed as `page * limit`: on a final short
 * page the arithmetic alone would claim more results than exist.
 *
 * @param {Object} props Component props.
 *
 * @returns {JSX.Element|null} The footer, or null when everything fits on one page.
 */
const SubscriptionsPagination = ( props ) => {
	const { total, page, limit, totalPages } = props.meta;

	// Nothing to navigate. The "showing X of Y" line goes with it: on a single page it only restates the
	// row count the user can already see.
	if ( totalPages <= 1 ) {
		return null;
	}

	// Both ends are clamped, not just the upper one. The container corrects a page number left past the end
	// of a shrunken result set, but that correction lands a render later — and until it does, an unclamped
	// lower bound reads "Showing 51 to 30 of 30 results".
	const to = Math.min( page * limit, total );
	const from = Math.min( ( ( page - 1 ) * limit ) + 1, to );

	return (
		<div className={ styles.paginationFooter }>
			<span className={ styles.showingResults }>
				<FormattedMessage { ...messages.showingResults } values={ { from, to, total } } />
			</span>
			<Pagination
				variant="text"
				current={ page }
				total={ totalPages }
				onNavigate={ props.onNavigate }
				screenReaderTextPrevious={ props.intl.formatMessage( messages.previousPage ) }
				screenReaderTextNext={ props.intl.formatMessage( messages.nextPage ) }
			/>
		</div>
	);
};

SubscriptionsPagination.propTypes = {
	meta: PropTypes.shape( {
		total: PropTypes.number.isRequired,
		page: PropTypes.number.isRequired,
		limit: PropTypes.number.isRequired,
		totalPages: PropTypes.number.isRequired,
	} ).isRequired,
	onNavigate: PropTypes.func.isRequired,
	intl: intlShape.isRequired,
};

export default injectIntl( SubscriptionsPagination );
